import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import DatePickerModule from "react-datetime-picker";
import { client_v2 } from "utils/api";
import { Modal, Loader, PackerStatisticsBar } from "components";

const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
  >
    <rect
      fill="#595d6e"
      width="15.833"
      height="3.333"
      rx="1"
      transform="translate(1.667 4.167)"
    />
    <rect
      fill="#595d6e"
      opacity="0.3"
      width="15.833"
      height="8.333"
      rx="1"
      transform="translate(1.667 9.167)"
    />
  </svg>
);
const cart_box_sizes = ["heavy", "none", "box_4", "box_6", "box_12", "box_24"];

const CompletedBoxModal = ({
  handleClose,
  default_date = moment().toDate(),
}) => {
  const [date, setDate] = useState(default_date);

  const query_date = useMemo(
    () => (date ? moment(date).format("DD-MM-YYYY") : ""),
    [date],
  );

  const {
    isLoading: is_loading_completed_by_pickers_and_packers_user_points,
    data: data_completed_by_pickers_and_packers_user_points = {},
  } = useQuery(
    ["dashboard_completed_by_pickers_and_packers_user_points", query_date],
    () =>
      client_v2.get(
        `/stats/dashboard_completed_by_pickers_and_packers_user_points${query_date ? `?date=${query_date}` : ""}`,
      ),
    {
      select: (res) => res.data,
      retry: false,
    },
  );
  const completed_data = useMemo(() => {
    if (
      Object.keys(data_completed_by_pickers_and_packers_user_points).length > 0
    ) {
      return Object.keys(data_completed_by_pickers_and_packers_user_points).map(
        (user_key) => {
          const packing = {};
          const picking = {};
          const storing = {};

          if (
            data_completed_by_pickers_and_packers_user_points?.[user_key]
              ?.packing &&
            Object.keys(
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.packing,
            ).length > 0
          ) {
            Object.keys(
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.packing,
            ).forEach((packing_key) => {
              packing[packing_key] =
                data_completed_by_pickers_and_packers_user_points?.[
                  user_key
                ]?.packing?.[packing_key];
            });
          }

          if (
            data_completed_by_pickers_and_packers_user_points?.[user_key]
              ?.picking &&
            Object.keys(
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.picking,
            ).length > 0
          ) {
            Object.keys(
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.picking,
            ).forEach((picking_key) => {
              picking[picking_key] =
                data_completed_by_pickers_and_packers_user_points?.[
                  user_key
                ]?.picking?.[picking_key];
            });
          }

          if (
            data_completed_by_pickers_and_packers_user_points?.[user_key]
              ?.storing &&
            Object.keys(
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.storing,
            ).length > 0
          ) {
            Object.keys(
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.storing,
            ).forEach((storing_key) => {
              storing[storing_key] =
                data_completed_by_pickers_and_packers_user_points?.[
                  user_key
                ]?.storing?.[storing_key];
            });
          }

          return {
            name: user_key,
            work_mode:
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.work_mode,
            packer_gauge:
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.packer_gauge,
            packing,
            picking,
            storing,
            total_points:
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.total_points || 0,
            worked_hours:
              data_completed_by_pickers_and_packers_user_points?.[user_key]
                ?.worked_hours || 0,
          };
        },
      );
    } else {
      return [];
    }
  }, [data_completed_by_pickers_and_packers_user_points]);

  return (
    <Modal
      size="full"
      handleClose={handleClose}
      header={{
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <circle
              fill="#0abb87"
              opacity="0.3"
              cx="10"
              cy="10"
              r="10"
              transform="translate(2 2)"
            />
            <path
              fill="#0abb87"
              d="M16.769,7.818a1,1,0,0,1,1.462,1.365l-7,7.5a1,1,0,0,1-1.382.077l-3.5-3a1,1,0,0,1,1.3-1.519l2.772,2.376Z"
            />
          </svg>
        ),
        title: "Zrealizowane",
      }}
    >
      {is_loading_completed_by_pickers_and_packers_user_points ? (
        <Loader />
      ) : (
        <div className="dashboard__modal-table" style={{ minHeight: 400 }}>
          <div className="header-wrapper">
            <p className="title">Statystyki pracowników</p>
            <div className="inp-wrapper">
              <div className="calendar-group">
                <DatePickerModule
                  disableClock={true}
                  calendarIcon={<CalendarIcon />}
                  onChange={(a) => setDate(a)}
                  value={date}
                  locale="pl-PL"
                  format="yyyy-MM-dd"
                  maxDetail="minute"
                />
              </div>
            </div>
          </div>
          {completed_data?.length > 0 && (
            <table>
              <tr className="main-head">
                <th width="220"></th>
                <th width="10%">Przyjmowanie dostaw</th>
                <th width="25%">Zbieranie</th>
                <th>Pakowanie</th>
                <th width="120"></th>
              </tr>
              <tr className="sub-head">
                <th>Imię i nazwisko</th>
                <th>
                  <table className="sub-head__table">
                    <tr>
                      <th width="66.6666%">Ilość rozniesionych produktów</th>
                      <th width="33.333%">Punkty</th>
                    </tr>
                  </table>
                </th>
                <th>
                  <table className="sub-head__table">
                    <tr>
                      <th width="33%">Ilość lokalizacji</th>
                      <th width="33%">Ilość sztuk</th>
                      <th width="33%">Punkty</th>
                    </tr>
                  </table>
                </th>
                <th>
                  <table className="sub-head__table with-padding">
                    <tr>
                      {cart_box_sizes.map((item) => (
                        <th key={item} width="14%">
                          {item === "none" ? "box_1" : item}
                          <table className="sub-head__table__table">
                            <tr>
                              <th width="50%">*</th>
                              <th width="50%">1</th>
                            </tr>
                          </table>
                        </th>
                      ))}
                      <th width="60px">Ilość zam.</th>
                      <th>Punkty</th>
                    </tr>
                  </table>
                </th>
                <th>
                  <strong>Suma</strong>
                </th>
                <th width="60px">Czas pracy</th>
              </tr>
              <tr className="table-row">
                <td>Razem</td>
                <td>
                  <table>
                    <tr>
                      <td width="66.6666%">
                        <strong>
                          {completed_data.reduce(
                            (acc, item) =>
                              acc + (item?.storing?.stored_quantity || 0),
                            0,
                          )}
                        </strong>
                      </td>
                      <td width="33.333%">
                        <strong>
                          {completed_data.reduce(
                            (acc, item) => acc + (item?.storing?.points || 0),
                            0,
                          )}
                        </strong>
                      </td>
                    </tr>
                  </table>
                </td>
                <td>
                  <table>
                    <tr>
                      <td width="33%">
                        <strong>
                          {completed_data.reduce(
                            (acc, item) =>
                              acc + (item?.picking?.gdn_lines_count || 0),
                            0,
                          )}
                        </strong>
                      </td>
                      <td width="33%">
                        <strong>
                          {completed_data.reduce(
                            (acc, item) =>
                              acc + (item?.picking?.gdn_lines_quantity || 0),
                            0,
                          )}
                        </strong>
                      </td>
                      <td width="33%">
                        <strong>
                          {completed_data.reduce(
                            (acc, item) => acc + (item?.picking?.points || 0),
                            0,
                          )}
                        </strong>
                      </td>
                    </tr>
                  </table>
                </td>
                <td>
                  <table>
                    <tr>
                      {cart_box_sizes.map((key) => (
                        <td key={key} width="14%">
                          <table>
                            <tr>
                              <td width="50%">
                                <strong>
                                  {completed_data.reduce(
                                    (acc, item) =>
                                      acc + (item?.packing?.[key] || 0),
                                    0,
                                  )}
                                </strong>
                              </td>
                              <td width="50%">
                                <strong>
                                  {completed_data.reduce(
                                    (acc, item) =>
                                      acc + (item?.packing?.[`${key}_s`] || 0),
                                    0,
                                  )}
                                </strong>
                              </td>
                            </tr>
                          </table>
                        </td>
                      ))}
                      <td width="60px">
                        <strong>
                          {completed_data.reduce(
                            (acc, item) =>
                              acc + (item?.packing?.total_gdns || 0),
                            0,
                          )}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {completed_data.reduce(
                            (acc, item) => acc + (item?.packing?.points || 0),
                            0,
                          )}
                        </strong>
                      </td>
                    </tr>
                  </table>
                </td>
                <td>
                  <strong>
                    {completed_data.reduce(
                      (acc, item) => acc + (item?.total_points || 0),
                      0,
                    )}
                  </strong>
                </td>
                <td>
                  <strong>
                    {completed_data
                      .reduce(
                        (acc, item) => acc + (Number(item?.worked_hours) || 0),
                        0,
                      )
                      .toFixed(2)}{" "}
                    h
                  </strong>
                </td>
              </tr>
              {completed_data
                .sort((a, b) => b.total_points - a.total_points)
                .map(
                  ({
                    name,
                    work_mode,
                    packer_gauge,
                    total_points,
                    picking,
                    packing,
                    storing,
                    worked_hours,
                  }) => (
                    <tr className="table-row">
                      <td>
                        <p className="m-b-5">
                          <span className="m-r-5">{name}</span>
                          {(work_mode === "floating_packer" ||
                            packer_gauge === "big") && (
                            <>
                              {work_mode === "floating_packer" && (
                                <span
                                  class="label label--info m-r-5"
                                  style={{ padding: "2px 6px" }}
                                >
                                  <span class="text-10">LOT</span>
                                </span>
                              )}
                              {packer_gauge === "big" && (
                                <span
                                  class="label label--info m-r-5"
                                  style={{ padding: "2px 6px" }}
                                >
                                  <span class="text-10">HEV</span>
                                </span>
                              )}
                            </>
                          )}
                        </p>
                        <PackerStatisticsBar
                          show_label={false}
                          packer_gauge="small"
                          value={total_points}
                        />
                      </td>
                      <td>
                        <table>
                          <tr>
                            <td width="66.6666%">{storing.stored_quantity}</td>
                            <td width="33.333%">{storing.points}</td>
                          </tr>
                        </table>
                      </td>
                      <td>
                        <table>
                          <tr>
                            <td width="33%">{picking.gdn_lines_count}</td>
                            <td width="33%">{picking.gdn_lines_quantity}</td>
                            <td width="33%">{picking.points}</td>
                          </tr>
                        </table>
                      </td>
                      <td>
                        <table>
                          <tr>
                            {cart_box_sizes.map((key) => (
                              <td key={key} width="14%">
                                <table>
                                  <tr>
                                    <td width="50%">{packing[key] || "-"}</td>
                                    <td width="50%">
                                      {packing[`${key}_s`] || "-"}
                                    </td>
                                  </tr>
                                </table>
                              </td>
                            ))}
                            <td width="60px">
                              <strong>{packing.total_gdns || 0}</strong>
                            </td>
                            <td>{packing.points}</td>
                          </tr>
                        </table>
                      </td>
                      <td>
                        <strong>{total_points}</strong>
                      </td>
                      <td>
                        <strong>{worked_hours} h</strong>
                      </td>
                    </tr>
                  ),
                )}
            </table>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CompletedBoxModal;
